/**
 * Admin Layout Component
 * Main layout wrapper for admin pages with sidebar navigation
 */

import React, { useState } from 'react';
import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom';
import { 
  LayoutDashboard,
  Users,
  Building,
  FileText,
  Briefcase,
  BarChart3,
  Settings,
  AlertTriangle,
  LogOut,
  Menu,
  X,
  Shield,
  Database,
  MessageSquare,
  Eye,
  Download
} from 'lucide-react';
import { logout } from '../../../services/firebase/authService';
import { useAdmin } from '../contexts/AdminContext';

const AdminLayout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser } = useAdmin();

  const navigationItems = [
    {
      name: 'Dashboard',
      href: '/admin/dashboard',
      icon: LayoutDashboard,
      description: 'System overview and metrics'
    },
    {
      name: 'Users',
      href: '/admin/users',
      icon: Users,
      description: 'Manage student accounts'
    },
    {
      name: 'Companies',
      href: '/admin/companies',
      icon: Building,
      description: 'Company verification and management'
    },
    {
      name: 'Job Postings',
      href: '/admin/jobs',
      icon: Briefcase,
      description: 'Monitor job postings'
    },
    {
      name: 'Resources',
      href: '/admin/resources',
      icon: FileText,
      description: 'Review and approve resources'
    },
    {
      name: 'Analytics',
      href: '/admin/analytics',
      icon: BarChart3,
      description: 'Platform analytics and insights'
    },
    {
      name: 'Content Moderation',
      href: '/admin/moderation',
      icon: Shield,
      description: 'Review reported content'
    },
    {
      name: 'System Health',
      href: '/admin/system',
      icon: Eye,
      description: 'System monitoring and logs'
    },
    {
      name: 'Data Export',
      href: '/admin/export',
      icon: Download,
      description: 'Export system data'
    },
    {
      name: 'Settings',
      href: '/admin/settings',
      icon: Settings,
      description: 'Admin settings and configuration'
    }
  ];

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const isActive = (href) => location.pathname === href;

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 z-50 bg-black bg-opacity-50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div className={`
        fixed inset-y-0 left-0 z-50 w-64 lg:w-72 bg-white shadow-lg transform transition-transform duration-300 ease-in-out
        lg:relative lg:transform-none lg:translate-x-0 lg:z-0
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div className="flex items-center space-x-3">
              <div className="relative">
                <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center transform rotate-12 shadow-lg">
                  <span className="text-white font-bold text-lg transform -rotate-12">V</span>
                </div>
                <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-400 rounded-full animate-pulse" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">
                  <span className="text-green-500">Varsity</span>Link Admin
                </h1>
                <p className="text-sm text-gray-600">System Administration</p>
              </div>
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden p-2 rounded-lg hover:bg-gray-100"
            >
              <X size={20} />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {navigationItems.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => setSidebarOpen(false)}
                  className={`
                    flex items-center space-x-3 px-4 py-3 rounded-lg transition-all duration-200
                    ${isActive(item.href)
                      ? 'bg-green-50 text-green-700 border-l-4 border-green-500'
                      : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                    }
                  `}
                >
                  <Icon size={20} className={isActive(item.href) ? 'text-green-600' : 'text-gray-500'} />
                  <div className="flex-1">
                    <div className="font-medium">{item.name}</div>
                    <div className="text-xs text-gray-500">{item.description}</div>
                  </div>
                </Link>
              );
            })}
          </nav>

          {/* User section */}
          <div className="p-4 border-t border-gray-200">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                <span className="text-green-600 font-medium">
                  {currentUser?.email?.charAt(0).toUpperCase() || 'A'}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {currentUser?.email || 'Admin'}
                </p>
                <p className="text-xs text-gray-500">System Administrator</p>
              </div>
            </div>
            
            <button
              onClick={handleLogout}
              className="w-full flex items-center space-x-3 px-4 py-3 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
            >
              <LogOut size={20} />
              <span>Sign Out</span>
            </button>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top bar */}
        <div className="bg-white shadow-sm border-b border-gray-200 z-40">
          <div className="flex items-center justify-between px-4 sm:px-6 py-4">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden p-2 rounded-lg hover:bg-gray-100"
              >
                <Menu size={20} />
              </button>
              
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
                  {navigationItems.find(item => isActive(item.href))?.name || 'Admin Panel'}
                </h1>
                <p className="text-sm sm:text-base text-gray-600">
                  {navigationItems.find(item => isActive(item.href))?.description || 'VarsityLink Administration'}
                </p>
              </div>
            </div>

            <div className="flex items-center space-x-4">
              {/* System status indicator */}
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                <span className="text-sm text-gray-600">System Online</span>
              </div>

              {/* Quick actions */}
              <div className="flex items-center space-x-2">
                <Link
                  to="/admin/moderation"
                  className="p-2 text-gray-400 hover:text-green-600 rounded-lg hover:bg-gray-100 transition-colors"
                  title="Content Moderation"
                >
                  <AlertTriangle size={20} />
                </Link>
                
                <Link
                  to="/admin/system"
                  className="p-2 text-gray-400 hover:text-green-600 rounded-lg hover:bg-gray-100 transition-colors"
                  title="System Health"
                >
                  <Database size={20} />
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Page content */}
        <main className="flex-1 overflow-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;